import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { App } from "antd";
import { Observer, observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useReducer } from "react";
import { Tree, createObserverTreeRenderItem, useFolderTree } from "@devowl-wp/react-folder-tree";
import { AppTreeAlerts } from "./appTreeAlerts.js";
import { MetaBox } from "./metaBox.js";
import { ProBox } from "./proFooter.js";
import { RearrangeBox } from "./rearrangeBox.js";
import { useAppTreeUiBundle } from "./useAppTreeUiBundle.js";
import { useAppTreeMountContext } from "../../contexts/appTreeMountContext.js";
import { RML_FOLDER_RENAMED } from "../../events/folderLifecycle.js";
import { useRmlMediaBridge } from "../../hooks/tree/bridge/useRmlMediaBridge.js";
import { useAttachmentSortable } from "../../hooks/tree/dnd/useAttachmentSortable.js";
import { useDragDropBinding } from "../../hooks/tree/dnd/useDragDropBinding.js";
import { useOrderHandlers } from "../../hooks/tree/toolbar/useOrderHandlers.js";
import { useRenderOrderMenu } from "../../hooks/tree/toolbar/useRenderOrderMenu.js";
import { useRenderSortMenu } from "../../hooks/tree/toolbar/useRenderSortMenu.js";
import { useSortHandlers } from "../../hooks/tree/toolbar/useSortHandlers.js";
import { useToolbarState } from "../../hooks/tree/toolbar/useToolbarState.js";
import { useTrashNode } from "../../hooks/tree/toolbar/useTrashNode.js";
import { useAppTreeDetails } from "../../hooks/tree/useAppTreeDetails.js";
import { useAppTreeMount } from "../../hooks/tree/useAppTreeMount.js";
import { useCreatable } from "../../hooks/tree/useCreatable.js";
import { useHandleSelect } from "../../hooks/tree/useHandleSelect.js";
import { useStores } from "../../store/rootStore.js";
import { TreeStore } from "../../store/treeStore.js";
import { __, _i } from "../../utils/i18n.js";
import { resolveIcon } from "../../utils/icons.js";
import { isMaterialWp, materialWpResizeOpposite } from "../../utils/materialWp.js";
const AppTreeBodyContent = observer(({ id, children, callbacksRef, onUiBundleChange, localState })=>{
    const { message } = App.useApp();
    const { treeStore } = useStores();
    const { attachmentsBrowser, isSticky, isStickyHeader, isResizable, isFullWidth, style } = useAppTreeMountContext();
    useAttachmentSortable();
    const { staticTree, tree, rootId, selected } = treeStore;
    const [, bumpSelectedRevision] = useReducer((revision)=>revision + 1, 0);
    const { showProFeature, setShowProFeature, rearrangeBoxId, setRearrangeBoxId } = localState;
    const { isSortableDisabled, isTreeBusy, toolbarActiveButton } = useFolderTree();
    const { attachmentFilterActive, handleOrderClick, handleOrderCancel } = useOrderHandlers({
        bindFilterListener: true
    });
    const { lockedVisible, lockedToolTipText, orderDisabled, renameDisabled, trashDisabled, sortDisabled, detailsDisabled } = useToolbarState(localState.isWPAttachmentsSortMode, selected, rootId, attachmentFilterActive);
    const renderOrderMenu = useRenderOrderMenu();
    const renderSortMenu = useRenderSortMenu();
    const { handleSelect } = useHandleSelect(bumpSelectedRevision);
    const handleReload = useCallback(()=>{
        handleSelect();
    }, [
        handleSelect
    ]);
    useRmlMediaBridge();
    const { handleTrashModifier, handleMultipleTrashModifier } = useTrashNode();
    const { prepareCreatableDraft, handleCreatablePersist, creatableButtons, getCreatableSelectedId, getCreatableRootPlacementIds, onCreatableStart, onCreatableSuccess, onCreatableError } = useCreatable();
    const { handleSort } = useSortHandlers();
    const { bindTreeRef, initDragDrop, refreshDroppable } = useDragDropBinding();
    const handleResize = useCallback(()=>{
        attachmentsBrowser && attachmentsBrowser.attachments?.setColumns();
    }, [
        attachmentsBrowser
    ]);
    const { metaBoxId, handleDetails } = useAppTreeDetails();
    useAppTreeMount();
    useEffect(()=>treeStore.subscribeSelectedChange(bumpSelectedRevision), [
        treeStore,
        bumpSelectedRevision
    ]);
    useEffect(()=>{
        refreshDroppable();
    }, [
        tree,
        staticTree,
        isTreeBusy,
        refreshDroppable
    ]);
    useEffect(()=>{
        initDragDrop();
    }, [
        localState.isMoveable,
        localState.isWPAttachmentsSortMode,
        toolbarActiveButton,
        initDragDrop
    ]);
    const handleCheck = useCallback((nodeId)=>{
        treeStore.getTreeItemById(nodeId)?.toggleChecked();
    }, [
        treeStore
    ]);
    const handleCloseMultiToolbar = useCallback((checkedNodes)=>{
        checkedNodes.forEach((n)=>treeStore.getTreeItemById(n.id)?.toggleChecked(false, false));
    }, [
        treeStore
    ]);
    const onTreeNodeRender = useMemo(()=>createObserverTreeRenderItem(Observer), []);
    const onTreeNodeRenderIcon = useCallback((icon)=>resolveIcon(icon), []);
    const handleTreeSort = useCallback(async ({ id: folderId, oldIndex, newIndex, parentFromId, parentToId, nextId, source })=>{
        return handleSort({
            id: +folderId,
            oldIndex,
            newIndex,
            parentFromId: +parentFromId,
            parentToId: +parentToId,
            nextId: nextId !== undefined ? +nextId : undefined,
            source
        });
    }, [
        handleSort
    ]);
    useAppTreeUiBundle({
        localState,
        toolbarActiveButton,
        creatableButtons,
        lockedVisible,
        lockedToolTipText,
        orderDisabled,
        renameDisabled,
        trashDisabled,
        sortDisabled,
        detailsDisabled,
        renderOrderMenu,
        renderSortMenu,
        handleOrderClick,
        handleOrderCancel,
        handleReload,
        handleTrashModifier,
        handleMultipleTrashModifier,
        handleDetails,
        selectedFolderType: treeStore.selected?.properties?.type,
        onUiBundleChange
    });
    callbacksRef.current = {
        renderItem: onTreeNodeRender,
        renderIcon: onTreeNodeRenderIcon,
        onSelect: handleSelect,
        onCloseMultiToolbar: handleCloseMultiToolbar,
        onCheck: handleCheck,
        onSort: handleTreeSort,
        onSortPersistStart: ()=>message.loading(__("Reordering the tree hierarchy...")),
        onSortPersistSuccess: ()=>message.success(__("Successfully sorted the tree hierarchy")),
        onSortPersistError: (error)=>message.error(error.responseJSON.message),
        onTreeStructureChanged: refreshDroppable,
        onResize: handleResize,
        onResizeOpposite: isMaterialWp() ? materialWpResizeOpposite : undefined,
        canRenameNode: (node)=>node.id !== TreeStore.ID_ALL,
        onRenamePersist: async (folderId, inputValue)=>{
            const node = treeStore.getTreeItemById(folderId);
            const { name } = await node.setName(inputValue);
            document.dispatchEvent(new CustomEvent(RML_FOLDER_RENAMED, {
                detail: {
                    id: +folderId,
                    name
                },
                bubbles: true
            }));
            return name;
        },
        onRenameStart: (inputValue)=>message.loading(_i(__("Renaming to {{strong}}%s{{/strong}}...", inputValue), {
                strong: /*#__PURE__*/ _jsx("strong", {})
            })),
        onRenameSuccess: (name)=>message.success(_i(__("Successfully renamed folder to {{strong}}%s{{/strong}}", name), {
                strong: /*#__PURE__*/ _jsx("strong", {})
            })),
        onRenameError: (error)=>message.error(error.responseJSON.message),
        onCreatablePersist: handleCreatablePersist,
        prepareCreatableDraft,
        getCreatableSelectedId,
        getCreatableRootPlacementIds,
        onCreatableStart,
        onCreatableSuccess,
        onCreatableError
    };
    const treeLayoutProps = {
        id,
        opposite: document.getElementById("wpbody-content"),
        innerClassName: "wrap",
        theme: "wordpress",
        isSticky,
        isStickyHeader,
        isResizable,
        isFullWidth,
        style,
        isSortable: true,
        sortableDelay: isSortableDisabled ? 150 : 0,
        headerStickyAttr: {
            top: "#wpadminbar"
        },
        forceSortableFallback: true
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Tree, {
                ref: bindTreeRef,
                ...treeLayoutProps,
                children: [
                    /*#__PURE__*/ _jsx(AppTreeAlerts, {}),
                    children
                ]
            }),
            /*#__PURE__*/ _jsx(ProBox, {
                feature: showProFeature,
                onClose: ()=>setShowProFeature(undefined)
            }),
            metaBoxId !== false && /*#__PURE__*/ _jsx(MetaBox, {
                id: metaBoxId,
                onClose: (status, response)=>handleDetails(undefined, status, response)
            }),
            /*#__PURE__*/ _jsx(RearrangeBox, {
                id: rearrangeBoxId || undefined,
                onClose: ()=>setRearrangeBoxId(undefined)
            })
        ]
    });
});
export { AppTreeBodyContent };
